// Обёртка Telegram WebApp с браузерным фолбэком. Компоненты используют только `tg`,
// window.Telegram напрямую не трогают.
import type { TgInitDataUnsafe, TgSafeAreaInset, TgWebApp } from './types'

export type HapticKind =
  | 'selection'
  | 'light'
  | 'medium'
  | 'heavy'
  | 'rigid'
  | 'soft'
  | 'success'
  | 'warning'
  | 'error'

/** Паттерны вибрации браузерного фолбэка — как hap() макета. */
export const VIBRATE_PATTERNS: Record<HapticKind, number | number[]> = {
  selection: 8,
  light: 12,
  soft: 12,
  medium: 20,
  rigid: 20,
  heavy: 25,
  success: [20, 60, 20],
  warning: [15, 40, 15],
  error: [30, 40, 30],
}

const ZERO_INSET: TgSafeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 }

function webApp(): TgWebApp | undefined {
  if (typeof window === 'undefined') return undefined
  return window.Telegram?.WebApp
}

function safe(fn: () => void): void {
  try {
    fn()
  } catch {
    /* старый клиент Telegram или браузер — молча пропускаем */
  }
}

function supports(app: TgWebApp, version: string): boolean {
  try {
    return app.isVersionAtLeast(version)
  } catch {
    return false
  }
}

function vibrate(pattern: number | number[]): void {
  safe(() => {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(pattern)
  })
}

function mediaDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return !window.matchMedia('(prefers-color-scheme: light)').matches
}

let backHandler: (() => void) | null = null

/* ---------- dev-эмуляция окружения (только dev-панель MOCK_MODE) ----------
 * 'telegram' — интерфейс ведёт себя как в Telegram (своя «Назад» скрыта, кнопку «Назад» рисует эмулированная
 * шапка через getEmulatedBack); 'browser' — как вне Telegram. null — реальное окружение.
 * Вход (initData) эмуляция не меняет. Хранится в sessionStorage вкладки. */
export type TgEmulation = 'telegram' | 'browser' | null

const EMU_KEY = 'dalat.dev.tg'
let emulation: TgEmulation = readEmulation()
let emuBack: (() => void) | null = null
const emuListeners = new Set<() => void>()

function readEmulation(): TgEmulation {
  try {
    const v = globalThis.sessionStorage?.getItem(EMU_KEY)
    return v === 'telegram' || v === 'browser' ? v : null
  } catch {
    return null
  }
}

function emuNotify(): void {
  emuListeners.forEach((l) => l())
}

export function getTgEmulation(): TgEmulation {
  return emulation
}

export function setTgEmulation(mode: TgEmulation): void {
  emulation = mode
  try {
    if (mode) globalThis.sessionStorage?.setItem(EMU_KEY, mode)
    else globalThis.sessionStorage?.removeItem(EMU_KEY)
  } catch {
    /* приватный режим */
  }
  if (mode !== 'telegram') emuBack = null
  emuNotify()
}

/** Обработчик «Назад», который приложение отдало кнопке Telegram при эмуляции 'telegram' (null — «Закрыть»). */
export function getEmulatedBack(): (() => void) | null {
  return emuBack
}

export function subscribeTgEmulation(l: () => void): () => void {
  emuListeners.add(l)
  return () => emuListeners.delete(l)
}

export const tg = {
  /** Открыто внутри Telegram (скрипт загружен и есть подписанный initData). */
  get isTelegram(): boolean {
    if (emulation) return emulation === 'telegram'
    const app = webApp()
    return Boolean(app && app.initData)
  },
  /** Сырой initData для `Authorization: tma <initData>`. Пусто вне Telegram. */
  get initData(): string {
    return webApp()?.initData ?? ''
  },
  get initDataUnsafe(): TgInitDataUnsafe {
    return webApp()?.initDataUnsafe ?? {}
  },
  get startParam(): string | undefined {
    const app = webApp()
    if (app?.initDataUnsafe?.start_param) return app.initDataUnsafe.start_param
    if (typeof window === 'undefined') return undefined
    return new URLSearchParams(window.location.search).get('tgWebAppStartParam') ?? undefined
  },
  get platform(): string {
    return webApp()?.platform ?? 'web'
  },
  get version(): string {
    return webApp()?.version ?? '0'
  },
  get colorScheme(): 'light' | 'dark' {
    const app = webApp()
    if (app && app.initData) return app.colorScheme === 'light' ? 'light' : 'dark'
    return mediaDark() ? 'dark' : 'light'
  },
  get safeAreaInset(): TgSafeAreaInset {
    return { ...ZERO_INSET, ...(webApp()?.safeAreaInset ?? {}) }
  },
  get contentSafeAreaInset(): TgSafeAreaInset {
    return { ...ZERO_INSET, ...(webApp()?.contentSafeAreaInset ?? {}) }
  },

  ready(): void {
    const app = webApp()
    if (app) safe(() => app.ready())
  },
  expand(): void {
    const app = webApp()
    if (app) safe(() => app.expand())
  },
  disableVerticalSwipes(): void {
    const app = webApp()
    if (app && supports(app, '7.7')) safe(() => app.disableVerticalSwipes?.())
  },
  close(): void {
    const app = webApp()
    if (app && app.initData) safe(() => app.close())
  },
  /** Цвет шапки и фона Telegram под тему приложения (hex). */
  setColors(header: string, background: string = header): void {
    const app = webApp()
    if (!app || !app.initData || !supports(app, '6.1')) return
    safe(() => app.setHeaderColor?.(header))
    safe(() => app.setBackgroundColor?.(background))
    if (supports(app, '7.10')) safe(() => app.setBottomBarColor?.(background))
  },

  /** Тактильный отклик; вне Telegram — navigator.vibrate. Число/массив — сырой паттерн. */
  haptic(kind: HapticKind | number | number[] = 'light'): void {
    const app = webApp()
    const h = app && app.initData && supports(app, '6.1') ? app.HapticFeedback : undefined
    if (typeof kind !== 'string') {
      if (h) safe(() => h.impactOccurred('light'))
      else vibrate(kind)
      return
    }
    if (!h) return vibrate(VIBRATE_PATTERNS[kind])
    safe(() => {
      if (kind === 'selection') h.selectionChanged()
      else if (kind === 'success' || kind === 'warning' || kind === 'error') h.notificationOccurred(kind)
      else h.impactOccurred(kind)
    })
  },

  backButton: {
    /** Есть ли нативная кнопка «Назад» (иначе страница рисует свою). */
    get isNative(): boolean {
      if (emulation) return emulation === 'telegram'
      const app = webApp()
      return Boolean(app && app.initData && supports(app, '6.1'))
    },
    /** Показать кнопку с единственным обработчиком (предыдущий снимается). */
    show(cb: () => void): void {
      if (emulation === 'telegram') {
        emuBack = cb
        emuNotify()
        return
      }
      if (emulation === 'browser') return
      const app = webApp()
      if (!app || !app.initData || !supports(app, '6.1')) return
      safe(() => {
        if (backHandler) app.BackButton.offClick(backHandler)
        backHandler = cb
        app.BackButton.onClick(cb)
        app.BackButton.show()
      })
    },
    hide(): void {
      if (emulation) {
        if (emuBack) {
          emuBack = null
          emuNotify()
        }
        return
      }
      const app = webApp()
      if (!app || !app.initData || !supports(app, '6.1')) return
      safe(() => {
        if (backHandler) app.BackButton.offClick(backHandler)
        backHandler = null
        app.BackButton.hide()
      })
    },
  },

  /** Внешняя ссылка (навигатор, сайт). */
  openLink(url: string): void {
    const app = webApp()
    if (app && app.initData) {
      safe(() => app.openLink(url))
      return
    }
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer')
  },
  /** Ссылка t.me (чат с мастерской, бот). Вне Telegram — новая вкладка. */
  openTelegramLink(url: string): void {
    const app = webApp()
    if (app && app.initData && /^https:\/\/t\.me\//.test(url)) {
      safe(() => app.openTelegramLink(url))
      return
    }
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer')
  },

  /**
   * Чат бота, из которого открыт Mini App (трансляция геопозиции, связь). openTelegramLink на тот же
   * чат Telegram игнорирует — чат уже «открыт» под приложением, поэтому приложение закрывается,
   * и курьер оказывается в чате. Вне Telegram — новая вкладка.
   */
  openBotChat(url: string): void {
    const app = webApp()
    if (app && app.initData && /^https:\/\/t\.me\//.test(url)) {
      safe(() => app.openTelegramLink(url))
      safe(() => app.close())
      return
    }
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer')
  },

  /** Подписка на смену темы (Telegram themeChanged или prefers-color-scheme). Возвращает отписку. */
  onThemeChange(cb: (scheme: 'light' | 'dark') => void): () => void {
    const app = webApp()
    if (app && app.initData) {
      const h = () => cb(app.colorScheme === 'light' ? 'light' : 'dark')
      safe(() => app.onEvent('themeChanged', h))
      return () => safe(() => app.offEvent('themeChanged', h))
    }
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const h = (e: MediaQueryListEvent) => cb(e.matches ? 'light' : 'dark')
    mq.addEventListener?.('change', h)
    return () => mq.removeEventListener?.('change', h)
  },
  /** Подписка на изменение высоты окна Mini App (раскрытие, полноэкранный режим). */
  onViewportChange(cb: () => void): () => void {
    const app = webApp()
    if (!app || !app.initData) return () => {}
    safe(() => app.onEvent('viewportChanged', cb))
    return () => safe(() => app.offEvent('viewportChanged', cb))
  },
  /** Подписка на изменение safe-area (Bot API 8.0). */
  onSafeAreaChange(cb: () => void): () => void {
    const app = webApp()
    if (!app || !app.initData) return () => {}
    safe(() => {
      app.onEvent('safeAreaChanged', cb)
      app.onEvent('contentSafeAreaChanged', cb)
    })
    return () =>
      safe(() => {
        app.offEvent('safeAreaChanged', cb)
        app.offEvent('contentSafeAreaChanged', cb)
      })
  },
}

export type Tg = typeof tg
